// AI Generated Component
import { Component, OnInit, Input } from '@angular/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { ControlContainer, NgForm } from '@angular/forms';
import { safeClone } from '@client/app/shared/utils/object-utils';

interface MatchRule {
    type: string;
    value?: string;
    accessibility_check_ids?: number[];
}

interface TypeOption {
    name: string;
    value: string;
}

interface CmsOption {
    name: string;
    id: string;
}

interface AccessibilityCheck {
    id: number;
    name: string;
}

@Component({
    selector: 'form-source-code-exclude-accessibility',
    standalone: false,
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
    template: `
        <fieldset [ngModelGroup]="modelGroup" #accessibilityForm="ngModelGroup">
            <div class="card panel-monsido-card">
                <div class="card-header">
                    <h3 class="font-weight-300" translate>Details</h3>
                </div>
                <div class="card-body">
                    <form-field-input [name]="'short_description'"
                                      [label]="'Short Description' | translate"
                                      [placeholder]="'Describe the exclude' | translate"
                                      type="text"
                                      [(ngModel)]="model.short_description"
                                      #shortDescription="ngModel"
                                      [required]="true">
                    </form-field-input>
                    <form-field-errors [input]="accessibilityForm.control?.controls?.short_description">
                    </form-field-errors>
                    <div class="form-group row mb-1 select-dropdown">
                        <label [attr.for]="'cms'"
                               class="col-sm-8 col-form-label text-bold capitalize-first-letter pt-1 text-right">
                            {{ 'Default for CMS' | translate }}
                        </label>
                        <div class="col-sm-40">
                            <angular-selector [name]="'cms'"
                                              [(ngModel)]="model.default_for_cms"
                                              [options]="cmses"
                                              [removeButton]="true"
                                              labelAttr="name"
                                              valueAttr="id"
                                              [placeholder]="'None' | translate">
                            </angular-selector>
                        </div>
                    </div>

                    <div class="row form-group"
                         *ngFor="let rule of getVisibleMatchRules(); let i = index; trackBy: trackByIndex">
                        <label class="col-sm-8 col-form-label text-bold text-right" [for]="'rule_type_' + i" translate>
                            Type*
                        </label>
                        <div class="col-sm-10 pr-sm-0 select-dropdown">
                            <angular-selector
                                    [monId]="'rule_type_' + i"
                                    [name]="'rule_type_' + i"
                                    [(ngModel)]="rule.type"
                                    [options]="typeOptions"
                                    labelAttr="name"
                                    valueAttr="value"
                                    [required]="true"
                                    [removeButton]="false"
                                    [multiple]="false">
                            </angular-selector>
                        </div>
                        <label class="col-sm-4 col-form-label text-bold text-right"
                               [for]="'rule_value_' + i"
                               translate>
                            Value*
                        </label>
                        <div class="col-sm-8 pr-sm-0">
                            <input type="text"
                                   class="form-control form-control-lg"
                                   [id]="'rule_value_' + i"
                                   [name]="'rule_value_' + i"
                                   [required]="true"
                                   [(ngModel)]="rule.value"
                            />
                        </div>
                        <label class="col-sm-4 col-form-label text-bold text-right"
                               [for]="'rule_accessibility_check_ids_' + i"
                               translate>
                            Checks
                        </label>
                        <div class="col-sm-11">
                            <angular-selector [multiple]="true"
                                    [monId]="'rule_accessibility_check_ids_' + i"
                                    [name]="'rule_accessibility_check_ids_' + i"
                                    [(ngModel)]="rule.accessibility_check_ids"
                                    labelAttr="name"
                                    valueAttr="id"
                                    [placeholder]="'Choose checks' | translate"
                                    [debounce]="300"
                                    remoteParam="search"
                                    [remote]="getAccessibilityChecks()"
                                    [dropdownItemTemplate]="'app/modules/source-code-excludes/form/source-code-exclude/modules/templates/dropdown-template.html'"
                                    [viewItemTemplate]="'app/modules/source-code-excludes/form/source-code-exclude/modules/templates/dropdown-template.html'">
                            </angular-selector>
                        </div>
                        <div class="col col-md-3 text-right">
                            <button class="btn btn-border-radius btn-secondary pt-2"
                                    type="button"
                                    [disabled]="model.match_rules.length <= 1"
                                    (click)="removeMatchRule(i)">
                                <span class="text-link" [ngClass]="['ACTIONS', 'REMOVE'] | monIcons"></span>
                            </button>
                        </div>
                    </div>
                    <div class="row">
                        <div class="offset-8 col-auto mr-auto">
                            <button type="button"
                                    class="btn btn-border-radius btn-secondary"
                                    (click)="addMatchRule()">
                                <span class="text-link" [ngClass]="['ACTIONS', 'ADD'] | monIcons" aria-hidden="true"></span>
                            </button>
                        </div>
                        <div class="col-auto" *ngIf="model.match_rules.length > pageSize">
                            <ngb-pagination
                                [disabled]="model.match_rules.length <= pageSize"
                                [collectionSize]="model.match_rules.length"
                                [(page)]="page"
                                [pageSize]="pageSize"
                                [maxSize]="4"
                                [boundaryLinks]="false"
                                size="sm">
                            </ngb-pagination>
                        </div>
                    </div>
                </div>
            </div>
        </fieldset>

        <ng-template #dropdownTemplate let-option="option">
            <span [innerHTML]="option.name"></span>
        </ng-template>
    `,
})
export class FormSourceCodeExcludeAccessibilityComponent implements OnInit {
    @Input() modelGroup = '';
    @Input() accessibilityChecksLoading: Promise<AccessibilityCheck[]> = Promise.resolve([]);
    @Input() model;

    pageSize = 10;
    page = 1;
    cmses: CmsOption[] = [];
    typeOptions: TypeOption[] = [];

    constructor (private translateService: TranslateService) {}

    ngOnInit (): void {
        this.initializeCmses();
        this.initializeDefaultValues();
        this.setupMatchRuleTypeOptions();
    }

    private initializeCmses (): void {
        this.cmses = safeClone([
            {
                name: 'CivicEngage Central',
                id: 'civicengage_central',
            },
            {
                name: 'CivicEngage Evolve',
                id: 'civicengage_evolve',
            },
            {
                name: 'Web Open',
                id: 'civicengage_open',
            },
            {
                name: 'Craft CMS',
                id: 'craft',
            },
            {
                name: 'SIMsite',
                id: 'simsite',
            },
            {
                name: 'Adobe CMS',
                id: 'adobe',
            },
            {
                name: 'Konform',
                id: 'konform',
            },
            {
                name: 'Acos',
                id: 'acos',
            },
            {
                name: 'AEM',
                id: 'aem',
            },
            {
                name: 'Shopify',
                id: 'shopify',
            },
            {
                name: 'EPiServer',
                id: 'episerver',
            },
            {
                name: 'iCreate',
                id: 'icreate',
            },
            {
                name: 'Elcom',
                id: 'elcom',
            },
            {
                name: 'Cascade',
                id: 'cascade',
            },
            {
                name: 'CorePublish',
                id: 'corepublish',
            },
            {
                name: 'Custom CMS',
                id: 'custom',
            },
            {
                name: 'Sitecore',
                id: 'sitecore',
            },
            {
                name: 'Dynamicweb',
                id: 'dynamicweb',
            },
            {
                name: 'Umbraco',
                id: 'umbraco',
            },
            {
                name: 'Custom meta header',
                id: 'special',
            },
            {
                name: 'WordPress',
                id: 'wordpress',
            },
            {
                name: 'WordPress Multisite',
                id: 'wordpress_multisite',
            },
            {
                name: 'Omniupdate',
                id: 'omniupdate',
            },
            {
                name: 'Publicera',
                id: 'publicera',
            },
            {
                name: 'Drupal',
                id: 'drupal',
            },
            {
                name: 'Finalsite',
                id: 'finalsite',
            },
            {
                name: 'Kentico',
                id: 'kentico',
            },
            {
                name: 'Municode',
                id: 'municode',
            },
            {
                name: 'Presence',
                id: 'presence',
            },
            {
                name: 'Gabbart',
                id: 'gabbart',
            },
            {
                name: 'Hubspot',
                id: 'hubspot',
            },
            {
                name: 'Squarespace',
                id: 'squarespace',
            },
            {
                name: 'Squiz',
                id: 'squiz',
            },
            {
                name: 'SilverStripe',
                id: 'silverstripe',
            },
            {
                name: 'Sitefinity',
                id: 'sitefinity',
            },
            {
                name: 'Sitevision',
                id: 'sitevision',
            },
            {
                name: 'Plone',
                id: 'plone',
            },
            {
                name: 'OpenCities',
                id: 'opencities',
            },
            {
                name: 'Joomla',
                id: 'joomla',
            },
            {
                name: 'Smart Sites',
                id: 'smart_sites',
            },
            {
                name: 'BayWSF',
                id: 'baywsf',
            },
        ].sort((a, b) => a.name > b.name ? 1 : a.name < b.name ? -1 : 0));
    }

    private initializeDefaultValues (): void {
        if (!Array.isArray(this.model.match_rules)) {
            this.model.match_rules = [];
        }

        if (typeof this.model.id !== 'number' && this.model.match_rules.length === 0) {
            this.addMatchRule();
        }
    }

    private setupMatchRuleTypeOptions (): void {
        this.typeOptions = [
            {
                name: this.translateService.getString('Exact'),
                value: 'exact',
            },
            {
                name: this.translateService.getString('Regex'),
                value: 'regex',
            },
            {
                name: this.translateService.getString('Contains'),
                value: 'contains',
            },
        ];
    }

    getAccessibilityChecks = (search?: string): Promise<AccessibilityCheck[]> => {
        return this.accessibilityChecksLoading ? this.accessibilityChecksLoading : Promise.resolve([]);
    };

    addMatchRule (): void {
        this.model.match_rules.unshift({ type: 'exact' });
        this.page = 1;
    }

    removeMatchRule (index: number): void {
        if (this.model.match_rules.length > 1) {
            const actualIndex = this.pageSize * (this.page - 1) + index;
            this.model.match_rules.splice(actualIndex, 1);
        }
    }

    getVisibleMatchRules (): MatchRule[] {
        const startIndex = this.pageSize * (this.page - 1);
        return this.model.match_rules.slice(startIndex, startIndex + this.pageSize);
    }

    trackByIndex (index: number): number {
        return index;
    }
}
